import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, FolderOpen, ChevronRight, Settings, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useProjects } from '../hooks/useProjects';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { CreateProjectModal } from '../components/projects';
import { PlanLimitWarning, UsageBadge } from '../components/billing';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export function ProjectsPage() {
  const navigate = useNavigate();
  const { projects, isLoadingProjects, projectsError, createProject, isCreatingProject, createProjectError, selectProject, primaryTeamId } = useProjects();
  const { projectsUsage, canCreateProject, refetchUsage } = usePlanLimits(primaryTeamId);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const isAtProjectLimit = !canCreateProject();

  const handleCreateProject = (data: { name: string; description?: string }) => {
    createProject(data, {
      onSuccess: (newProject) => {
        setIsCreateModalOpen(false);
        selectProject(newProject.id);
        refetchUsage(); // Update usage counts after creating
        navigate(`/projects/${newProject.id}`);
      },
    });
  };

  const handleOpenCreateModal = () => {
    if (isAtProjectLimit) {
      // The modal will show upgrade options via the LimitExceededProvider
      return;
    }
    setIsCreateModalOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between gap-4 mb-5"
      >
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold tracking-tight">Projects</h2>
          {projectsUsage ? (
            <UsageBadge usage={projectsUsage} />
          ) : (
            <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
              {projects.length}
            </span>
          )}
        </div>
        <Button
          size="sm"
          onClick={handleOpenCreateModal}
          disabled={isAtProjectLimit}
          className="gap-1.5 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-shadow disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          New Project
        </Button>
      </motion.div>

      <AnimatePresence mode="wait">
        {isLoadingProjects ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="glass overflow-hidden">
              <CardContent className="p-0">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex border-b border-border/50 last:border-b-0">
                    <div className="flex-1 px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-muted/50 animate-shimmer" />
                        <div className="space-y-2 flex-1">
                          <div className="h-4 w-32 bg-muted/50 rounded animate-shimmer" />
                          <div className="h-3 w-20 bg-muted/30 rounded animate-shimmer" />
                        </div>
                      </div>
                    </div>
                    <div className="w-32 px-4 py-4 hidden sm:block">
                      <div className="h-4 w-16 bg-muted/30 rounded animate-shimmer" />
                    </div>
                    <div className="w-24 px-4 py-4 hidden md:block">
                      <div className="h-4 w-14 bg-muted/30 rounded animate-shimmer" />
                    </div>
                    <div className="w-20 px-4 py-4">
                      <div className="h-4 w-12 bg-muted/30 rounded animate-shimmer ml-auto" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ) : projectsError ? (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="glass">
              <CardContent className="p-12 text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, type: 'spring', stiffness: 300, damping: 25 }}
                  className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-destructive/10 mb-4"
                >
                  <FolderOpen className="h-6 w-6 text-destructive" />
                </motion.div>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-sm text-destructive"
                >
                  Failed to load projects
                </motion.p>
              </CardContent>
            </Card>
          </motion.div>
        ) : projects.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="glass">
              <CardContent className="p-12 text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, type: 'spring', stiffness: 300, damping: 25 }}
                  className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-muted/50 mb-4"
                >
                  <FolderOpen className="h-6 w-6 text-muted-foreground" />
                </motion.div>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-muted-foreground mb-4"
                >
                  No projects yet
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button
                    size="sm"
                    onClick={handleOpenCreateModal}
                    disabled={isAtProjectLimit}
                    className="gap-1.5 shadow-lg shadow-primary/20"
                  >
                    <Plus className="h-4 w-4" />
                    Create project
                  </Button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="glass overflow-hidden">
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead className="bg-muted/20 border-b border-border/50">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Name</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">Slug</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Created</th>
                      <th className="w-20 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {projects.map((project, index) => (
                      <motion.tr
                        key={project.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                        whileHover={{ backgroundColor: 'hsl(var(--muted) / 0.3)' }}
                        className="transition-colors group"
                      >
                        <td className="px-4 py-3">
                          <Link 
                            to={`/projects/${project.id}`}
                            onClick={() => selectProject(project.id)}
                            className="flex items-center gap-3 hover:text-primary transition-colors"
                          >
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="flex items-center justify-center w-9 h-9 rounded-lg bg-muted/50 group-hover:bg-primary/10 transition-colors"
                            >
                              <FolderOpen className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                            </motion.div>
                            <motion.span 
                              whileHover={{ x: 2 }}
                              className="font-medium"
                            >
                              {project.name}
                            </motion.span>
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground font-mono hidden sm:table-cell">
                          {project.slug}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                          {new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <motion.div whileTap={{ scale: 0.95 }}>
                              <Link 
                                to={`/projects/${project.id}/settings`}
                                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
                              >
                                <Settings className="h-3.5 w-3.5" />
                              </Link>
                            </motion.div>
                            <motion.div whileTap={{ scale: 0.95 }}>
                              <Link 
                                to={`/projects/${project.id}`}
                                onClick={() => selectProject(project.id)}
                                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
                              >
                                <ChevronRight className="h-3.5 w-3.5" />
                              </Link>
                            </motion.div>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Limit warning banner */}
      {projectsUsage && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4"
        >
          <PlanLimitWarning
            resourceType="projects"
            usage={projectsUsage}
            variant="banner"
            onUpgradeClick={() => navigate('/billing')}
          />
        </motion.div>
      )}

      {/* Floating create button - hide when at limit */}
      {!isAtProjectLimit && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 300, damping: 25 }}
          className="fixed bottom-8 right-8"
        >
          <Button
            size="lg"
            className="gap-2 shadow-2xl shadow-primary/30 hover:shadow-primary/40 transition-all hover:scale-105 active:scale-95"
            onClick={handleOpenCreateModal}
          >
            <Sparkles className="h-4 w-4" />
            New Project
          </Button>
        </motion.div>
      )}

      <CreateProjectModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSubmit={handleCreateProject} isLoading={isCreatingProject} error={createProjectError} />
    </div>
  );
}
